import { useEffect, useState } from 'react';
import { useList, useCustomMutation, useInvalidate, useGo } from '@refinedev/core';
import { Plus, Loader2, CreditCard, Link2, Copy } from 'lucide-react';
import { notify } from '@/lib/notify';
import { apiClient } from '@/providers/api-client';
import { SessionSelector } from '@/components/shared/SessionSelector';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatCurrency, formatDate, EmptyValue } from '@/components/universal';
import { PaymentStatus } from '@masters-club/types';
import type { Order } from '../types';
import { PaymentDocumentsUpload } from '@/resources/payments/components/PaymentDocumentsUpload';

interface CurrencyRecord {
  code: string;
  name: string;
  symbol: string;
  isActive: boolean;
}

interface PaymentRecord {
  id: number;
  orderId: number;
  amount: string | number;
  currency: string;
  status: string;
  provider: string;
  providerPaymentId: string | null;
  paidAt: string | null;
  createdAt: string;
}

const PAYMENT_STATUS_MAP: Record<string, { label: string; className: string }> = {
  CONFIRMED: { label: 'Подтверждён', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  REJECTED: { label: 'Отклонён', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
  PENDING: { label: 'Ожидает', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  REFUNDED: { label: 'Возврат', className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  PARTIALLY_REFUNDED: { label: 'Частичный возврат', className: 'bg-violet-500/10 text-violet-600 border-violet-500/20' },
};

const PROVIDER_STYLE: Record<string, string> = {
  CLICK: 'bg-blue-500/10 text-blue-700 border-blue-500/20',
  PAYME: 'bg-cyan-500/10 text-cyan-700 border-cyan-500/20',
  STRIPE: 'bg-violet-500/10 text-violet-700 border-violet-500/20',
  MANUAL: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  MULTICARD: 'bg-orange-500/10 text-orange-700 border-orange-500/20',
};

function renderPaymentStatus(value: string | undefined | null) {
  if (!value) {
    return <Badge variant="outline" className="text-[11px] px-1.5 py-0">--</Badge>;
  }
  const config = PAYMENT_STATUS_MAP[value.toUpperCase()];
  return (
    <Badge variant="outline" className={cn('text-[11px] px-1.5 py-0', config?.className)}>
      {config?.label ?? value}
    </Badge>
  );
}

interface PaymentConfig {
  multicardEnabled: boolean;
  cloudPaymentsEnabled: boolean;
}

type InvoiceProvider = 'multicard' | 'cloudpayments';
const INVOICE_DELIVERIES = ['copy', 'telegram', 'email', 'telegram_session'] as const;
type InvoiceDelivery = (typeof INVOICE_DELIVERIES)[number];

const isInvoiceDelivery = (v: string): v is InvoiceDelivery =>
  v === 'copy' || v === 'telegram' || v === 'email' || v === 'telegram_session';

const INVOICE_PROVIDER_LABEL: Record<InvoiceProvider, string> = {
  multicard: 'Rahmat (Multicard)',
  cloudpayments: 'CloudPayments',
};

interface CreatePaymentFormState {
  amount: string;
  currency: string;
}

const INITIAL_FORM_STATE: CreatePaymentFormState = {
  amount: '',
  currency: 'UZS',
};

export function OrderPaymentsTab({
  order,
  onRefetch,
}: {
  order: Order;
  onRefetch: () => void;
}) {
  const go = useGo();
  const invalidate = useInvalidate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [form, setForm] = useState<CreatePaymentFormState>({ ...INITIAL_FORM_STATE, currency: order.currency });
  const [pendingDocFileIds, setPendingDocFileIds] = useState<number[]>([]);

  // --- Создание ссылки на оплату (инвойс) ---
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [invoiceProvider, setInvoiceProvider] = useState<InvoiceProvider | null>(null);
  const [invoiceDelivery, setInvoiceDelivery] = useState<InvoiceDelivery>('copy');
  const [invoiceSessionId, setInvoiceSessionId] = useState<number | null>(null);
  const [invoiceGenerating, setInvoiceGenerating] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);
  const [invoiceDeliveryNote, setInvoiceDeliveryNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<PaymentConfig>('/payment-config')
      .then((cfg) => {
        if (cancelled) { return; }
        setPaymentConfig(cfg);
        // Дефолтный провайдер — первый из настроенных
        if (cfg.multicardEnabled) {
          setInvoiceProvider('multicard');
        } else if (cfg.cloudPaymentsEnabled) {
          setInvoiceProvider('cloudpayments');
        }
      })
      .catch(() => {
        if (!cancelled) { setPaymentConfig(null); }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const availableProviders: InvoiceProvider[] = [];
  if (paymentConfig?.multicardEnabled) { availableProviders.push('multicard'); }
  if (paymentConfig?.cloudPaymentsEnabled) { availableProviders.push('cloudpayments'); }

  const handleGenerateInvoice = async () => {
    if (!invoiceProvider) { return; }
    setInvoiceGenerating(true);
    setInvoiceUrl(null);
    setInvoiceDeliveryNote(null);
    try {
      const result = await apiClient.post<{
        paymentUrl: string;
        delivered: boolean;
        deliveryError: string | null;
      }>(`/orders/${order.id}/invoice`, {
        provider: invoiceProvider,
        delivery: invoiceDelivery,
        ...(invoiceDelivery === 'telegram_session' && invoiceSessionId !== null
          ? { sessionId: invoiceSessionId }
          : {}),
      });
      setInvoiceUrl(result.paymentUrl);
      if (invoiceDelivery === 'copy') {
        notify.success('Ссылка на оплату создана');
      } else if (result.delivered) {
        notify.success(
          invoiceDelivery === 'telegram'
            ? 'Ссылка отправлена в Telegram'
            : invoiceDelivery === 'telegram_session'
              ? 'Ссылка отправлена через Telegram-сессию'
              : 'Ссылка отправлена на email',
        );
      } else {
        setInvoiceDeliveryNote(result.deliveryError ?? 'Не удалось доставить ссылку');
        notify.error(result.deliveryError ?? 'Ссылка создана, но не доставлена');
      }
      query?.refetch();
      invalidate({ resource: 'payments', invalidates: ['list'] });
    } catch (error) {
      notify.error(error instanceof Error ? error.message : 'Не удалось создать ссылку');
    } finally {
      setInvoiceGenerating(false);
    }
  };

  const handleCopyInvoiceUrl = async () => {
    if (!invoiceUrl) { return; }
    try {
      await navigator.clipboard.writeText(invoiceUrl);
      notify.success('Скопировано в буфер обмена');
    } catch {
      notify.error('Не удалось скопировать');
    }
  };

  const { mutate: createPayment, mutation: createMutation } = useCustomMutation();
  const isCreating = createMutation.isPending;

  const { result, query } = useList<PaymentRecord>({
    resource: 'payments',
    filters: [{ field: 'orderId', operator: 'eq', value: order.id }],
    pagination: { pageSize: 50 },
  });

  const payments = result?.data ?? [];

  // Payment totals per order currency: only confirmed payments in the order's currency count as paid.
  const orderTotal = Number.parseFloat(order.total);
  const paidTotal = payments
    .filter((p) => p.status === PaymentStatus.CONFIRMED && p.currency === order.currency)
    .reduce((sum, p) => sum + Number(p.amount), 0);
  const remaining = Math.max(0, orderTotal - paidTotal);
  const isFullyPaid = paidTotal >= orderTotal && orderTotal > 0;

  // Load active currencies from API
  const currenciesQuery = useList<CurrencyRecord>({
    resource: 'currencies',
    pagination: { mode: 'off' },
    filters: [{ field: 'isActive', operator: 'eq', value: true }],
  });
  const currencies = currenciesQuery.result?.data ?? [];

  const handleFormChange = <K extends keyof CreatePaymentFormState>(
    field: K,
    value: CreatePaymentFormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = () => {
    const parsedAmount = parseFloat(form.amount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      return;
    }

    createPayment(
      {
        url: '/payments',
        method: 'post',
        values: {
          orderId: order.id,
          amount: form.amount,
          currency: form.currency,
          provider: 'MANUAL',
          status: PaymentStatus.CONFIRMED,
          documentFileIds: pendingDocFileIds.length > 0 ? pendingDocFileIds : undefined,
        },
      },
      {
        onSuccess: () => {
          setIsDialogOpen(false);
          setForm({ ...INITIAL_FORM_STATE, currency: order.currency });
          setPendingDocFileIds([]);
          query?.refetch();
          invalidate({ resource: 'payments', invalidates: ['list'] });
          invalidate({ resource: 'orders', invalidates: ['list', 'detail'] });
          onRefetch();
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader className="p-3 pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <CreditCard className="h-3.5 w-3.5" />
            Платежи
            {payments.length > 0 && (
              <Badge variant="secondary" className="text-[11px] px-1.5 py-0 font-normal">
                {payments.length}
              </Badge>
            )}
          </CardTitle>

          <div className="flex items-center gap-2">
            {availableProviders.length > 0 && (
              <Dialog
                open={isInvoiceDialogOpen}
                onOpenChange={setIsInvoiceDialogOpen}
              >
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-xs">
                    <Link2 className="h-3 w-3 mr-1" />
                    Создать ссылку на оплату
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Ссылка на оплату</DialogTitle>
                    <DialogDescription>
                      Сгенерируйте ссылку на оплату заказа #{order.id}. После
                      оплаты доступ выдаётся автоматически.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4 py-2">
                    <div className="space-y-2">
                      <Label className="text-sm">Платёжный провайдер</Label>
                      <Select
                        value={invoiceProvider ?? ''}
                        onValueChange={(v) =>
                          setInvoiceProvider(v as InvoiceProvider)
                        }
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Выберите провайдера..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableProviders.map((p) => (
                            <SelectItem key={p} value={p}>
                              {INVOICE_PROVIDER_LABEL[p]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Способ отправки</Label>
                      <Select
                        value={invoiceDelivery}
                        onValueChange={(v) => {
                          if (isInvoiceDelivery(v)) {
                            setInvoiceDelivery(v);
                          }
                        }}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="copy">Скопировать ссылку</SelectItem>
                          <SelectItem value="telegram">
                            Отправить в Telegram (бот)
                          </SelectItem>
                          <SelectItem value="telegram_session">
                            Отправить через Telegram-сессию
                          </SelectItem>
                          <SelectItem value="email">Отправить на email</SelectItem>
                        </SelectContent>
                      </Select>
                      {invoiceDelivery === 'telegram_session' && (
                        <div className="mt-2 space-y-1">
                          <Label className="text-sm">Telegram-сессия</Label>
                          <SessionSelector
                            value={invoiceSessionId}
                            onChange={setInvoiceSessionId}
                            enabled={invoiceDelivery === 'telegram_session'}
                          />
                        </div>
                      )}
                    </div>

                    {invoiceUrl && (
                      <div className="space-y-2">
                        <Label className="text-sm">Ссылка на оплату</Label>
                        <div className="flex gap-2">
                          <Input
                            readOnly
                            value={invoiceUrl}
                            className="h-9 font-mono text-xs"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 shrink-0"
                            onClick={handleCopyInvoiceUrl}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        {invoiceDeliveryNote && (
                          <p className="text-xs text-destructive">
                            {invoiceDeliveryNote}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setIsInvoiceDialogOpen(false)}
                      disabled={invoiceGenerating}
                      className="h-8 text-xs"
                    >
                      Закрыть
                    </Button>
                    <Button
                      onClick={handleGenerateInvoice}
                      disabled={invoiceGenerating || !invoiceProvider}
                      className="h-8 text-xs"
                    >
                      {invoiceGenerating && (
                        <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
                      )}
                      {invoiceUrl ? 'Создать заново' : 'Создать ссылку'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 text-xs">
                <Plus className="h-3 w-3 mr-1" />
                Добавить платёж
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Создать платёж</DialogTitle>
                <DialogDescription>
                  Ручное создание платежа для заказа #{order.id}
                </DialogDescription>
              </DialogHeader>

              {/* Payment summary: total / paid / remaining */}
              <div className="grid grid-cols-3 gap-2 rounded-lg bg-muted/40 p-3 text-xs">
                <div className="flex flex-col gap-0.5">
                  <span className="text-muted-foreground">Итого</span>
                  <span className="font-mono font-semibold">{formatCurrency(orderTotal, order.currency)}</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-muted-foreground">Оплачено</span>
                  <span className={cn(
                    'font-mono font-semibold',
                    paidTotal > 0 ? 'text-emerald-600' : 'text-muted-foreground',
                  )}>
                    {formatCurrency(paidTotal, order.currency)}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-muted-foreground">Осталось</span>
                  <span className={cn(
                    'font-mono font-semibold',
                    isFullyPaid ? 'text-emerald-600' : remaining > 0 ? 'text-amber-600' : 'text-muted-foreground',
                  )}>
                    {isFullyPaid ? '—' : formatCurrency(remaining, order.currency)}
                  </span>
                </div>
              </div>

              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="payment-amount" className="text-sm">Сумма</Label>
                  <Input
                    id="payment-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={form.amount}
                    onChange={(e) => handleFormChange('amount', e.target.value)}
                    className="h-9"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Валюта</Label>
                  <Select
                    value={form.currency}
                    onValueChange={(v) => handleFormChange('currency', v)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.symbol} {c.code} — {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Документы</Label>
                  <PaymentDocumentsUpload
                    paymentId={null}
                    onPendingChange={setPendingDocFileIds}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={isCreating}
                  className="h-8 text-xs"
                >
                  Отмена
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={isCreating || !form.amount || parseFloat(form.amount) <= 0}
                  className="h-8 text-xs"
                >
                  {isCreating && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
                  Создать
                </Button>
              </DialogFooter>
            </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-3 pt-0">
        {payments.length === 0 ? (
          <div className="text-muted-foreground text-sm py-4 text-center">
            Нет платежей
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Сумма</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Провайдер</TableHead>
                <TableHead>Дата оплаты</TableHead>
                <TableHead>Создан</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow
                  key={p.id}
                  className="cursor-pointer"
                  onClick={() => go({ to: `/payments/show/${p.id}` })}
                >
                  <TableCell className="font-mono text-xs">#{p.id}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {formatCurrency(String(p.amount), p.currency)}
                  </TableCell>
                  <TableCell>{renderPaymentStatus(p.status)}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn('text-[11px] px-1.5 py-0', PROVIDER_STYLE[p.provider.toUpperCase()])}
                    >
                      {p.provider}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {p.paidAt ? formatDate(p.paidAt) : <EmptyValue />}
                  </TableCell>
                  <TableCell className="text-xs">
                    {formatDate(p.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
